import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import type { StandaloneLayeredMedia } from '@/modules/kinema/layeredMediaService';
import { Paths } from '@/paths';

interface ProjectCardProps {
  project: StandaloneLayeredMedia & { preview_url?: string };
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onDuplicate: (id: number) => Promise<void>;
}

export function ProjectCard({ project, onRename, onDelete, onDuplicate }: ProjectCardProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name);

  const root = project.data?.layers?.[project.data?.rootLayerId];
  const sizeLabel = root ? `${root.width}×${root.height}` : '';
  const durationLabel = root?.duration != null ? `${root.duration}s` : '';

  return (
    <div
      onClick={() => !editing && navigate(`/${Paths.CREATE}/projects/${project.id}`)}
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        cursor: 'pointer',
        width: 240,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <div
        style={{
          height: 135,
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        {project.preview_url ? (
          <img
            src={project.preview_url}
            alt={project.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ color: '#94a3b8', fontSize: 12 }}>No preview yet</div>
        )}
      </div>
      <div style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        {editing ? (
          <input
            value={name}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setName(e.target.value)}
            onBlur={async () => {
              if (name && name !== project.name) await onRename(project.id, name);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            style={{ flex: 1, padding: 4 }}
          />
        ) : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#1e293b' }}
          >
            {project.name}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <button
            aria-label="more"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                right: 0,
                top: 24,
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                minWidth: 120,
                zIndex: 10,
              }}
            >
              <button
                onClick={() => {
                  onDuplicate(project.id);
                  setMenuOpen(false);
                }}
                style={menuItemStyle}
              >
                Duplicate
              </button>
              <button
                onClick={() => {
                  onDelete(project.id);
                  setMenuOpen(false);
                }}
                style={{ ...menuItemStyle, color: '#ef4444' }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '0 10px 10px', fontSize: 11, color: '#94a3b8' }}>
        {sizeLabel}
        {sizeLabel && durationLabel ? ' · ' : ''}
        {durationLabel}
      </div>
    </div>
  );
}

const menuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  fontSize: 12,
  cursor: 'pointer',
  color: '#334155',
};
